import { getPipelineUserClickFrequencyLocalStorageKey } from '@/helpers/localStorageKeyHelper';
import { useLocalStorage } from '@payaca/hooks';
import { useCallback, useMemo, useRef, useState } from 'react';

const MIN_VISIBLE_DEFAULT = 4;

type ClickFrequency = Record<string, number>;

type UserItem = {
  id: string;
  name?: string | null;
};

type UseUserVisibilityPreferencesOptions<T extends UserItem> = {
  users: T[];
  selectedIds: string[];
  minVisible?: number;
  preferredUserIds?: string[];
};

export function useUserVisibilityPreferences<T extends UserItem>({
  users,
  selectedIds,
  minVisible = MIN_VISIBLE_DEFAULT,
  preferredUserIds = [],
}: UseUserVisibilityPreferencesOptions<T>) {
  // track users that should remain visible during this session --> stay visible until user is off page
  const [sessionVisibleIds, setSessionVisibleIds] = useState<Set<string>>(
    () => new Set(selectedIds)
  );

  // Track if we've initialized the session visible users
  const hasInitializedRef = useRef(false);

  // track click frequency from localStorage
  const [clickFrequency, setClickFrequency] = useLocalStorage<ClickFrequency>(
    getPipelineUserClickFrequencyLocalStorageKey(),
    {}
  );

  const recordClick = useCallback(
    (userId: string) => {
      setClickFrequency((prev) => ({
        ...prev,
        [userId]: ((prev ?? {})[userId] ?? 0) + 1,
      }));
      setSessionVisibleIds((prev) => new Set([...prev, userId]));
    },
    [setClickFrequency]
  );

  // calculate which users should be visible
  const visibleUsers = useMemo(() => {
    const scored = users.map((user) => {
      const isSelected = selectedIds.includes(user.id);
      const isSessionVisible = sessionVisibleIds.has(user.id);
      const isPreferred = preferredUserIds.includes(user.id);
      const frequency = clickFrequency?.[user.id] ?? 0;
      const name = (user.name ?? '').toLowerCase();

      return {
        user,
        isSelected,
        isSessionVisible,
        isPreferred,
        frequency,
        name,
      };
    });

    // Sort by ---> preferred first --> then selected --> then session-visible --> then frequency --> then alphabetical
    scored.sort((a, b) => {
      // preferred
      if (a.isPreferred !== b.isPreferred) {
        return a.isPreferred ? -1 : 1;
      }

      // session-visible users stay visible (even if unselected)
      if (a.isSessionVisible !== b.isSessionVisible) {
        return a.isSessionVisible ? -1 : 1;
      }

      // selected
      if (a.isSelected !== b.isSelected) {
        return a.isSelected ? -1 : 1;
      }

      // frequency of clicks
      if (a.frequency !== b.frequency) {
        return b.frequency - a.frequency;
      }

      // alphabetical
      return a.name.localeCompare(b.name);
    });

    // Calculate how many to show: at least minVisible, plus any session-visible users
    const sessionVisibleCount = scored.filter((s) => s.isSessionVisible).length;
    const visibleCount = Math.max(minVisible, sessionVisibleCount);

    const result = scored.slice(0, visibleCount).map((s) => s.user);

    // On first calculation, add all initially visible users to session
    if (!hasInitializedRef.current && result.length > 0) {
      hasInitializedRef.current = true;
      const initialIds = result.map((u) => u.id);
      // Use setTimeout to avoid state update during render
      setTimeout(() => {
        setSessionVisibleIds((prev) => new Set([...prev, ...initialIds]));
      }, 0);
    }

    return result;
  }, [
    users,
    selectedIds,
    sessionVisibleIds,
    clickFrequency,
    minVisible,
    preferredUserIds,
  ]);

  return {
    visibleUsers,
    recordClick,
  };
}
